import {
  EllipsisVerticalIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PermissionGuard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";
import { formatPrice } from "@/utils/monetary-formatter";

import { isInfinityAssistanceProduct } from "../products.assistance-variant";
import { EProductType, TProduct } from "../products.type";

type TWarrantyTheftPreviewRow = {
  id: string;
  /** Linhas de capital/faixa (omitidas quando falsas para Garantia / Roubo só com JSON). */
  showCapital: boolean;
  capitalValue: number;
  isAssistance: boolean;
  minimumPrice?: number;
  premiumValue: number;
  netPremium: number | null | undefined;
  commissionedPrice: number | null | undefined;
  premiumPriceType?: "net" | "commissioned";
  /** Só nome/descrição (plano adicional sem preço na raiz). */
  planOnly?: boolean;
  planName: string;
  description: string | null;
};

function getProductPreviewRows(p: TProduct): TWarrantyTheftPreviewRow[] {
  const isWarrantyTheft =
    p.type === EProductType.WARRANTY_INSURANCE ||
    p.type === EProductType.THEFT_INSURANCE;

  if (isWarrantyTheft) {
    const cfg =
      p.type === EProductType.WARRANTY_INSURANCE
        ? p.warranty_product_config
        : p.theft_product_config;
    const cfgObj =
      cfg && typeof cfg === "object" ? (cfg as Record<string, unknown>) : null;

    const hasLegacyRanges =
      Array.isArray(p.capital_ranges) && p.capital_ranges.length > 0;

    if (!hasLegacyRanges && cfgObj) {
      let premiumType = cfgObj.premium_price_type as string | undefined;
      const netPrice =
        typeof cfgObj.net_price === "number" ? cfgObj.net_price : null;
      const commissioned =
        typeof cfgObj.commissioned_price === "number"
          ? cfgObj.commissioned_price
          : null;

      if (premiumType !== "net" && premiumType !== "commissioned") {
        if (commissioned != null) {
          premiumType = "commissioned";
        } else if (netPrice != null) {
          premiumType = "net";
        }
      }

      const plans = Array.isArray(cfgObj.plans)
        ? (cfgObj.plans as Array<Record<string, unknown>>)
        : [];

      const rows: TWarrantyTheftPreviewRow[] = [];

      if (premiumType === "net" || premiumType === "commissioned") {
        rows.push({
          id: `${p.id}-premium-root`,
          showCapital: false,
          capitalValue: 0,
          isAssistance: false,
          premiumValue:
            premiumType === "commissioned"
              ? commissioned ?? 0
              : netPrice ?? 0,
          netPremium: netPrice,
          commissionedPrice: commissioned,
          premiumPriceType: premiumType,
          planName: p.name || "—",
          description: null,
        });
      }

      plans.forEach((plan, idx) => {
        rows.push({
          id: `${p.id}-plan-${idx}`,
          showCapital: false,
          capitalValue: 0,
          isAssistance: false,
          premiumValue: 0,
          netPremium: undefined,
          commissionedPrice: undefined,
          premiumPriceType: undefined,
          planOnly: true,
          planName: String(plan.name ?? `Plano ${idx + 1}`),
          description:
            plan.description != null ? String(plan.description) : null,
        });
      });

      return rows.length > 0 ? rows : [];
    }
  }

  return (p.capital_ranges || []).map((capital) => ({
    id: capital.id,
    showCapital: true,
    capitalValue: capital.capital_value,
    isAssistance: p.type === EProductType.INSURANCE_ASSISTANCE,
    minimumPrice: capital.minimum_price ?? undefined,
    premiumValue: capital.premium_value,
    netPremium:
      capital.net_premium ??
      (typeof capital.net_price === "number" ? capital.net_price : null),
    commissionedPrice: null,
    premiumPriceType: undefined,
    planName:
      (capital as { plan_name?: string | null }).plan_name || p.name || "—",
    description: capital.description,
  }));
}

interface ITableProductsProps {
  products: TProduct[] | undefined;
  deleteProduct: (id: string) => void;
}

export const TableProducts: React.FC<ITableProductsProps> = ({
  products,
  deleteProduct,
}) => {
  const { t } = useI18n();
  const { hasAnyPermission } = usePermissions();
  const [openRowId, setOpenRowId] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const handleRowToggle = (productId: string) => {
    setOpenRowId(openRowId === productId ? null : productId);
  };

  // Verificar se o usuário tem pelo menos uma permissão para ações
  const hasActionPermissions = hasAnyPermission(["products:edit", "products:delete"]);

  return (
    <div>
      <Card className="p-4">
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>{t("products.table.name")}</TableHead>
                <TableHead>{t("products.table.supplier")}</TableHead>
                <TableHead className="text-center">{t("products.table.type")}</TableHead>
                <TableHead className="text-center">{t("products.table.vigencyType")}</TableHead>
                <TableHead className="text-center">{t("products.table.lastModified")}</TableHead>
                {hasActionPermissions && (
                  <TableHead>{t("products.table.actions")}</TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {products?.map((p) => (
                <React.Fragment key={p.id}>
                  <TableRow>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleRowToggle(p.id)}
                      >
                        {openRowId === p.id ? (
                          <MinusIcon className="h-4 w-4" />
                        ) : (
                          <PlusIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      {p.insurance_company_name?.trim()
                        ? p.insurance_company_name
                        : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {p.type === EProductType.INSURANCE_ASSISTANCE
                        ? isInfinityAssistanceProduct(p)
                          ? "Assistências - Infinity"
                          : "Assistência - Soulhub"
                        : t(`product.type.${p.type}`)}
                    </TableCell>
                    <TableCell className="text-center">
                      {t(`product.type_of_vigency.${p.type_of_vigency}`)}
                    </TableCell>
                    <TableCell className="text-center">
                      {new Date(p.updated_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    {hasActionPermissions && (
                      <TableCell>
                        <PermissionGuard
                          permissions={["products:edit", "products:delete"]}
                          requireAll={false}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <Button size={"sm"} variant={"ghost"}>
                                <EllipsisVerticalIcon className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <PermissionGuard permission="products:edit">
                              <DropdownMenuItem
                                onSelect={() => navigate(`/products/${p.id}`)}
                              >
                                  Editar
                                </DropdownMenuItem>
                              </PermissionGuard>
                              <PermissionGuard permission="products:delete">
                                <DropdownMenuItem
                                  className="bg-red-400"
                                  onSelect={() => {
                                    deleteProduct(p.id);
                                  }}
                                >
                                  <TrashIcon className="h-4 w-4 mr-2" />
                                  {t("generic.delete")}
                                </DropdownMenuItem>
                              </PermissionGuard>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </PermissionGuard>
                      </TableCell>
                    )}
                  </TableRow>
                  {openRowId === p.id &&
                    (() => {
                      const previewRows = getProductPreviewRows(p);
                      return (
                    <TableRow>
                      <TableCell colSpan={hasActionPermissions ? 7 : 6} className="p-0">
                        <div className="p-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {previewRows.length === 0 ? (
                              <p className="text-sm text-muted-foreground col-span-full">
                                {t("products.table.expandEmpty")}
                              </p>
                            ) : (
                              previewRows.map((capital) => {
                                const isWarrantyTheftCfg =
                                  !capital.planOnly &&
                                  !capital.showCapital &&
                                  capital.premiumPriceType != null;
                                const isTheft =
                                  p.type === EProductType.THEFT_INSURANCE;
                                return (
                                  <Card key={capital.id} className="p-4">
                                    <CardContent className="p-0">
                                      <div className="space-y-2">
                                        {capital.planOnly ? (
                                          <div className="pt-1 border-t border-gray-200 space-y-1">
                                            <p className="text-xs text-gray-600">
                                              <span className="text-gray-500">
                                                {t("products.table.planName")}{" "}
                                              </span>
                                              {capital.planName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              <span className="text-gray-500">
                                                {t("products.table.planDescription")}{" "}
                                              </span>
                                              {capital.description ||
                                                t("products.table.noDescription")}
                                            </p>
                                          </div>
                                        ) : isWarrantyTheftCfg ? (
                                          <>
                                            {capital.premiumPriceType === "net" &&
                                              capital.netPremium != null && (
                                                <div className="flex justify-between items-center">
                                                  <span className="text-sm font-medium text-gray-600">
                                                    {isTheft
                                                      ? t("products.theft.premium.net")
                                                      : t("products.warranty.premium.net")}
                                                  </span>
                                                  <span className="text-sm font-semibold text-emerald-600">
                                                    {formatPrice(capital.netPremium)}
                                                  </span>
                                                </div>
                                              )}
                                            {capital.premiumPriceType === "commissioned" && (
                                              <>
                                                {capital.commissionedPrice != null && (
                                                  <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-600">
                                                      {isTheft
                                                        ? t(
                                                            "products.theft.fields.commissioned_price"
                                                          )
                                                        : t(
                                                            "products.warranty.fields.commissioned_price"
                                                          )}
                                                    </span>
                                                    <span className="text-sm font-semibold text-blue-600">
                                                      {formatPrice(
                                                        capital.commissionedPrice
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                                {capital.netPremium != null && (
                                                  <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-600">
                                                      {isTheft
                                                        ? t("products.theft.premium.net")
                                                        : t(
                                                            "products.warranty.premium.net"
                                                          )}
                                                    </span>
                                                    <span className="text-sm font-semibold text-emerald-600">
                                                      {formatPrice(capital.netPremium)}
                                                    </span>
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </>
                                        ) : (
                                          <>
                                            {capital.showCapital && (
                                              <div className="flex justify-between items-center">
                                                <span className="text-sm font-medium text-gray-600">
                                                  {p.type ===
                                                  EProductType.INSURANCE_ASSISTANCE
                                                    ? t(
                                                        "products.table.minimumValue"
                                                      )
                                                    : t("products.table.capital")}
                                                </span>
                                                <span className="text-sm font-semibold text-green-600">
                                                  {formatPrice(
                                                    capital.isAssistance
                                                      ? capital.minimumPrice ?? 0
                                                      : capital.capitalValue
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                            <div className="flex justify-between items-center">
                                              <span className="text-sm font-medium text-gray-600">
                                                {p.type ===
                                                EProductType.INSURANCE_ASSISTANCE
                                                  ? t("products.table.costValue")
                                                  : t("products.table.premium")}
                                              </span>
                                              <span className="text-sm font-semibold text-blue-600">
                                                {formatPrice(capital.premiumValue)}
                                              </span>
                                            </div>
                                            {capital.netPremium != null && (
                                              <div className="flex justify-between items-center">
                                                <span className="text-sm font-medium text-gray-600">
                                                  {t("products.table.netPremium")}
                                                </span>
                                                <span className="text-sm font-semibold text-emerald-600">
                                                  {formatPrice(capital.netPremium)}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                        {!capital.planOnly && (
                                          <div className="pt-2 border-t border-gray-200">
                                            <p className="text-xs text-gray-600">
                                              <span className="text-gray-500">
                                                {t("products.table.previewName")}{" "}
                                              </span>
                                              {capital.planName || t("products.table.noName")}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                              <span className="text-gray-500">
                                                {t("products.table.previewDescription")}{" "}
                                              </span>
                                              {capital.description ||
                                                t("products.table.noDescription")}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                      );
                    })()}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
